import React, { useEffect, useState } from "react";
import { Plus, Trash2, Copy, Users, Loader2, CheckCircle2, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const inputCls = "w-full bg-white border border-clay-300 px-2 py-1.5 text-sm outline-none focus:border-clay-900";
const round2 = (n) => Math.round(n * 100) / 100;

function statusOf(s) {
  if (s.fully_paid) return { text: "Completo", cls: "bg-pine-soft/40 text-pine" };
  if (s.deposit_paid) return { text: "Depósito pagado", cls: "bg-amber-100 text-amber-800" };
  if (s.paid > 0) return { text: "Parcial", cls: "bg-amber-50 text-amber-700" };
  return { text: "Sin pagar", cls: "bg-clay-100 text-clay-700" };
}

/** Agent-side editor + tracker for the custom per-traveler split. Amounts
 *  are in the trip's charge currency. Saved via PATCH payer_shares. */
export function PayerSharesBlock({ itineraryId, currency, total, shares, paymentUrl, onSaved }) {
  const fmt = (n) => new Intl.NumberFormat(currency === "EUR" ? "es-ES" : "en-US", { style: "currency", currency, maximumFractionDigits: 2 }).format(n || 0);
  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(Math.max(2, shares.length || 2));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setRows(shares.map((s) => ({ share_id: s.share_id, name: s.name, email: s.email || "", amount: String(s.amount) })));
  }, [shares]);

  const sum = round2(rows.reduce((a, r) => a + (parseFloat(r.amount) || 0), 0));
  const diff = round2(sum - (total || 0));
  const balanced = Math.abs(diff) < 1;
  const valid = rows.length >= 2 && rows.every((r) => r.name.trim() && parseFloat(r.amount) > 0);

  const setRow = (i, patch) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const splitEqually = () => {
    const n = Math.max(2, Math.min(30, parseInt(count) || 2));
    const each = Math.floor(((total || 0) / n) * 100) / 100;
    const last = round2((total || 0) - each * (n - 1));
    setRows((rs) => Array.from({ length: n }, (_, i) => ({
      share_id: rs[i]?.share_id, name: rs[i]?.name || "", email: rs[i]?.email || "",
      amount: String(i === n - 1 ? last : each),
    })));
  };
  const save = async (list) => {
    setSaving(true);
    try {
      await api.patch(`/itineraries/${itineraryId}`, {
        payer_shares: list.map((r) => ({ ...(r.share_id ? { share_id: r.share_id } : {}), name: r.name.trim(), email: r.email?.trim() || null, amount: round2(parseFloat(r.amount) || 0) })),
      });
      toast.success(list.length ? "Reparto guardado" : "Reparto eliminado");
      setEditing(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar el reparto");
    } finally { setSaving(false); }
  };
  const copyLink = async (s) => {
    try { await navigator.clipboard.writeText(`${paymentUrl}?payer=${s.share_id}`); toast.success(`Enlace de ${s.name} copiado`); }
    catch { toast.error("No se pudo copiar"); }
  };

  return (
    <div data-testid="payer-shares-block">
      <div className="smallcaps mb-2 flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5"><Users size={11}/> Reparto por viajero {shares.length > 0 && `(${shares.length})`}</span>
        {!editing && (
          <button onClick={() => { if (!rows.length) setRows([{ name: "", email: "", amount: "" }, { name: "", email: "", amount: "" }]); setEditing(true); }}
                  data-testid="shares-edit-btn"
                  className="text-[10px] uppercase tracking-widest text-clay-700 hover:text-terracotta inline-flex items-center gap-1">
            <Pencil size={11}/> {shares.length ? "Editar" : "Crear reparto"}
          </button>
        )}
      </div>

      {!editing && shares.length === 0 && (
        <div className="text-xs text-clay-500 italic px-3 py-3 border border-dashed border-clay-300">
          Sin reparto: el cliente ve el flujo normal (depósito 30 % / total, o dividir a partes iguales).
          Crea un reparto cuando cada viajero paga un importe distinto — cada uno verá solo su parte y su 30 %.
        </div>
      )}

      {!editing && shares.length > 0 && (
        <div className="border border-clay-300">
          <div className="grid grid-cols-[1fr_100px_100px_100px_120px_32px] gap-2 px-3 py-1.5 bg-clay-50 border-b border-clay-300 text-[10px] uppercase tracking-widest text-clay-700">
            <div>Viajero</div><div className="text-right">Parte</div><div className="text-right">Pagado</div><div className="text-right">Pendiente</div><div>Estado</div><div/>
          </div>
          {shares.map((s) => {
            const st = statusOf(s);
            return (
              <div key={s.share_id} data-testid={`share-row-${s.share_id}`}
                   className="grid grid-cols-[1fr_100px_100px_100px_120px_32px] gap-2 px-3 py-2 border-b border-clay-200 last:border-b-0 text-sm items-center">
                <div className="truncate">{s.name}{s.email && <span className="text-[10px] text-clay-500 ml-1.5">{s.email}</span>}</div>
                <div className="tabular text-right">{fmt(s.amount)}</div>
                <div className="tabular text-right text-pine">{fmt(s.paid)}</div>
                <div className="tabular text-right text-terracotta">{fmt(s.remaining)}</div>
                <div><span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest ${st.cls}`}>{s.fully_paid && <CheckCircle2 size={10}/>}{st.text}</span></div>
                <button onClick={() => copyLink(s)} title="Copiar enlace individual (nombre preseleccionado)" data-testid={`share-copy-link-${s.share_id}`}
                        className="p-1.5 border border-clay-300 hover:bg-clay-100 text-clay-700"><Copy size={12}/></button>
              </div>
            );
          })}
          <div className={`px-3 py-2 text-[11px] flex items-center justify-between ${balanced ? "text-clay-600" : "text-amber-800 bg-amber-50"}`} data-testid="shares-sum">
            <span>Suma de las partes: <span className="tabular font-medium">{fmt(sum)}</span> · Total viaje: <span className="tabular">{fmt(total)}</span></span>
            {!balanced && <span>Diferencia {diff > 0 ? "+" : ""}{fmt(diff)}</span>}
          </div>
        </div>
      )}

      {editing && (
        <div className="border border-clay-300 p-3 space-y-3" data-testid="shares-editor">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="text-clay-700">Repartir a partes iguales entre</span>
            <input type="number" min={2} max={30} value={count} onChange={(e) => setCount(e.target.value)} data-testid="shares-equal-count"
                   className="w-16 bg-white border border-clay-300 px-2 py-1 text-sm tabular text-center"/>
            <button onClick={splitEqually} data-testid="shares-equal-btn" className="px-2 py-1 border border-clay-300 hover:bg-clay-100">Repartir</button>
            <span className="text-clay-500">y ajusta los importes a mano.</span>
          </div>
          <div className="grid grid-cols-[1fr_1fr_120px_28px] gap-2 text-[10px] uppercase tracking-widest text-clay-700">
            <div>Nombre</div><div>Email (opcional)</div><div className="text-right">Importe ({currency})</div><div/>
          </div>
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_120px_28px] gap-2 items-center" data-testid={`share-edit-row-${i}`}>
              <input value={r.name} onChange={(e) => setRow(i, { name: e.target.value })} placeholder={`Viajero ${i + 1}`} data-testid={`share-edit-name-${i}`} className={inputCls}/>
              <input value={r.email} onChange={(e) => setRow(i, { email: e.target.value })} placeholder="email" data-testid={`share-edit-email-${i}`} className={inputCls}/>
              <input value={r.amount} onChange={(e) => setRow(i, { amount: e.target.value })} inputMode="decimal" data-testid={`share-edit-amount-${i}`} className={`${inputCls} text-right tabular`}/>
              <button onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))} disabled={rows.length <= 1} data-testid={`share-edit-remove-${i}`}
                      className="p-1 text-clay-500 hover:text-destructive disabled:opacity-30"><Trash2 size={13}/></button>
            </div>
          ))}
          <button onClick={() => setRows((rs) => [...rs, { name: "", email: "", amount: "" }])} data-testid="shares-add-row"
                  className="inline-flex items-center gap-1 text-xs text-clay-700 hover:text-terracotta"><Plus size={12}/> Añadir viajero</button>
          <div className={`text-xs px-3 py-2 border ${balanced ? "border-pine-soft bg-pine-soft/20 text-pine" : "border-amber-300 bg-amber-50 text-amber-900"}`} data-testid="shares-editor-sum">
            Suma <span className="tabular font-medium">{fmt(sum)}</span> · Total viaje <span className="tabular">{fmt(total)}</span>
            {balanced ? " · cuadra" : ` · diferencia ${diff > 0 ? "+" : ""}${fmt(diff)}`}
          </div>
          <div className="flex items-center justify-between gap-2">
            {shares.length > 0 ? (
              <button onClick={() => { if (window.confirm("¿Quitar el reparto? El cliente volverá a ver el flujo normal. Los pagos ya hechos se conservan.")) save([]); }}
                      disabled={saving} data-testid="shares-remove-btn" className="text-xs text-clay-600 hover:text-destructive inline-flex items-center gap-1"><X size={12}/> Quitar reparto</button>
            ) : <span/>}
            <div className="flex items-center gap-2">
              <button onClick={() => setEditing(false)} disabled={saving} className="px-3 py-1.5 text-xs border border-clay-300 hover:bg-clay-100" data-testid="shares-cancel-btn">Cancelar</button>
              <button onClick={() => save(rows)} disabled={saving || !valid} data-testid="shares-save-btn"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-clay-900 text-white hover:bg-terracotta disabled:opacity-50">
                {saving ? <Loader2 size={12} className="animate-spin"/> : <CheckCircle2 size={12}/>} Guardar reparto
              </button>
            </div>
          </div>
          {!balanced && valid && <div className="text-[11px] text-amber-800">Puedes guardar aunque no cuadre, pero lo normal es que la suma iguale el total.</div>}
        </div>
      )}
    </div>
  );
}
